import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { ChatService } from '../../services/chat.service';
import { ConversationResponse } from '../../models/response.module';
import { AuthService } from '../../../@core/services/auth.service';

@Component({
  selector: 'app-chat-dropdown',
  standalone: false,
  templateUrl: './chat-dropdown.component.html',
  styleUrl: './chat-dropdown.component.css'
})
export class ChatDropdownComponent implements OnInit, OnDestroy {
  conversations: ConversationResponse[] = [];
  unreadCount: number = 0;
  isLoading: boolean = false;
  isDropdownOpen: boolean = false;
  currentUserId: string = '';
  private unreadSubscription: Subscription;
  private conversationPollingSubscription: Subscription | null = null;

  constructor(
    private chatService: ChatService,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit() {
    const token = this.authService.getToken();
    this.currentUserId = token?.user_id || '';

    this.unreadSubscription = this.chatService.unreadCount.subscribe(count => {
      this.unreadCount = count;
    });
    this.chatService.startPollingUnreadCount(15000); // Poll every 15 seconds
    this.loadConversations();
    this.startConversationPolling();
  }

  ngOnDestroy() {
    this.chatService.stopPollingUnreadCount();
    this.stopConversationPolling();
    if (this.unreadSubscription) {
      this.unreadSubscription.unsubscribe();
    }
  }

  startConversationPolling() {
    this.conversationPollingSubscription = interval(15000).subscribe(() => {
      this.loadConversations(true);
    });
  }

  stopConversationPolling() {
    if (this.conversationPollingSubscription) {
      this.conversationPollingSubscription.unsubscribe();
      this.conversationPollingSubscription = null;
    }
  }

  loadConversations(silent: boolean = false) {
    if (!silent) {
      this.isLoading = true;
    }
    this.chatService.getConversations({ limit: 5 }).subscribe({
      next: (data) => {
        this.conversations = data.data || [];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  onDropdownToggle() {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.loadConversations();
    }
  }

  openConversation(conversation: ConversationResponse) {
    this.router.navigate(['/chat'], { queryParams: { conversation: conversation.conversation_id } });
    this.isDropdownOpen = false;
  }

  openAllChats() {
    this.router.navigate(['/chat']);
    this.isDropdownOpen = false;
  }

  getConversationTitle(conversation: ConversationResponse): string {
    if (conversation.subject) {
      return conversation.subject;
    }
    if (conversation.event) {
      return conversation.event.event_name;
    }
    if (conversation.company) {
      return conversation.company.company_name;
    }
    return 'Conversation';
  }

  getLastMessagePreview(conversation: ConversationResponse): string {
    if (conversation.last_message) {
      const text = conversation.last_message.message_text;
      return text.length > 50 ? text.substring(0, 50) + '...' : text;
    }
    return 'No messages yet';
  }

  getTimeAgo(date: Date | string | null | undefined): string {
    if (!date) return '';
    const now = new Date();
    const past = new Date(date);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString();
  }

  // Context helpers
  getConversationContext(conversation: ConversationResponse): 'event' | 'organization' | 'direct' {
    if (conversation.event_id || conversation.event) {
      return 'event';
    }
    if (conversation.company_id || conversation.company) {
      return 'organization';
    }
    return 'direct';
  }

  getContextIcon(conversation: ConversationResponse): string {
    const context = this.getConversationContext(conversation);
    switch (context) {
      case 'event': return 'fa-calendar-alt';
      case 'organization': return 'fa-building';
      default: return 'fa-user';
    }
  }

  getContextLabel(conversation: ConversationResponse): string {
    const context = this.getConversationContext(conversation);
    switch (context) {
      case 'event': return 'chat.contextEvent';
      case 'organization': return 'chat.contextOrganization';
      default: return 'chat.contextDirect';
    }
  }

  // Participant helpers
  getOtherParticipants(conversation: ConversationResponse): any[] {
    if (!conversation.participants) return [];
    return conversation.participants.filter(p => p.user_id.toString() !== this.currentUserId);
  }

  getParticipantNames(conversation: ConversationResponse): string {
    const others = this.getOtherParticipants(conversation);
    if (others.length === 0) return '';
    const names = others.slice(0, 2).map(p => {
      if (p.user) {
        return p.user.first_name;
      }
      return '';
    }).filter(n => n);
    if (others.length > 2) {
      return names.join(', ') + ` +${others.length - 2}`;
    }
    return names.join(', ');
  }
}
